import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import MainLayout from 'layouts/MainLayout';
import ErrorLayout from 'layouts/ErrorLayout';
import Loading from 'components/Loading';
import { ROUTE_PATH, ROUTE_STARTUP_PATH, ROUTE_TYPE_REDIRECT } from 'routes';
import { Navigate, useMatch } from 'react-router-dom';
import { startupCan } from 'utils/functions';
import { useSelector } from 'react-redux';
import { TOOL_CAP_TABLE, TOOL_PERFORMANCE } from 'constants/tools';
import usePreComponent from '../hooks/usePreComponent';
import InviteFounderButton from './InviteFounderButton';
import { usePageView } from '../hooks/usePageView';
import ChatBot from './ChatBot';

function RenderView({
  setGlobalProperties,
  route,
  isAuthenticated,
  platform,
}) {
  const { BASE, setEmbed } = ROUTE_STARTUP_PATH;
  const literalsCommon = useSelector(state => state.i18n.literals.common);
  const startup = useSelector(state => state.global.startup);

  const match = useMatch(route.path);
  usePageView(platform);
  usePreComponent({
    startup,
    match,
    setGlobalProperties,
    literalsCommon,
  });

  const isEmbed = useMemo(() => route?.path?.startsWith('/embed'), [route?.path]);
  const isUnauthorized = useMemo(() => route.permission && match.params?.id && !startupCan(route.permission.level, startup, route.permission.tool), [route.permission, match.params?.id, startup]);
  const shouldRedirectToLogin = !route.ignoreSession && !isAuthenticated;
  const isLoading = useMemo(() => (match?.pathname?.startsWith(BASE) || match?.pathname?.startsWith(setEmbed(BASE))) && match.params.id !== (startup?.id || startup?.externalId), [match?.pathname, BASE, setEmbed, match.params.id, startup]);

  if (shouldRedirectToLogin) return <Navigate to={ROUTE_PATH.LOGIN} />;
  if (route.type === ROUTE_TYPE_REDIRECT) {
    return ( // Mantener layout
      <MainLayout route={route}>
        <Navigate to={route.to} replace />
      </MainLayout>
    );
  }

  return (
    <MainLayout route={route}>
      <ChatBot isAuthenticated={isAuthenticated} />
      {isLoading && <Loading mode='panel' />}
      {!isLoading && isUnauthorized && <ErrorLayout code={401} />}
      {!isLoading && !isUnauthorized && (
        <>
          {React.isValidElement(route.component) ? route.component : (
            <route.component
              embed={isEmbed}
              match={match}
              route={route.path}
              {...route.props}
            />
          )}
          {isEmbed && !startup?.founder
            && [TOOL_CAP_TABLE, TOOL_PERFORMANCE].includes(route.embed) && (
            <InviteFounderButton
              literalsCommon={literalsCommon}
              startup={startup}
            />
          )}
        </>
      )}
    </MainLayout>
  );
}

RenderView.propTypes = {
  user: PropTypes.object.isRequired,
  setGlobalProperties: PropTypes.func.isRequired,
  route: PropTypes.object.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  platform: PropTypes.object.isRequired,
};

export default RenderView;
